import type { ReservationStatus } from "@vidimo/shared/enums";
import { User } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn, reservationStatusColors } from "@/lib/utils";

const sizeConfig = {
  "2xl": {
    className: "h-24 w-24",
    imgSize: 96,
    ringWidth: 4,
    textSize: "text-2xl",
  },
  lg: {
    className: "h-12 w-12",
    imgSize: 48,
    ringWidth: 2,
    textSize: "text-base",
  },
  md: {
    className: "h-10 w-10",
    imgSize: 40,
    ringWidth: 2,
    textSize: "text-sm",
  },
  sm: { className: "h-8 w-8", imgSize: 32, ringWidth: 2, textSize: "text-xs" },
  xl: {
    className: "h-16 w-16",
    imgSize: 64,
    ringWidth: 3,
    textSize: "text-xl",
  },
} as const;

export interface PersonAvatarProps {
  /** Alt text for the image (optional) */
  alt?: string;
  /** Avatar image URL */
  avatar?: string;
  /** Additional CSS classes */
  className?: string;
  /** Initials to show when no avatar image (when empty, shows User icon) */
  initials?: string;
  /** Reservation status to color the fallback background and/or ring */
  reservationStatus?: ReservationStatus;
  /** Size variant */
  size?: keyof typeof sizeConfig;
}

export function PersonAvatar({
  initials,
  avatar,
  alt = "",
  size = "lg",
  className,
  reservationStatus,
}: PersonAvatarProps) {
  const config = sizeConfig[size];
  const initialsStr = initials?.trim();
  const fallbackContent = initialsStr ? (
    <span className={cn("font-semibold", config.textSize)}>{initialsStr}</span>
  ) : (
    <User className={cn("text-primary", config.textSize)} />
  );

  const statusColor = reservationStatus
    ? reservationStatusColors[reservationStatus]
    : null;

  // Doughnut ring around the avatar when a reservation status is present.
  // We use `outline` (not an inset box-shadow) because the avatar's <img>
  // child is fully opaque — box-shadow paints below content and would get
  // hidden behind the image. Outlines paint above descendants, so the ring
  // stays visible whether the avatar renders an image or an initials
  // fallback. Negative outline-offset keeps the ring flush with the outer
  // edge (no layout shift).
  const ringStyle = statusColor
    ? {
        outlineColor: statusColor.hex,
        outlineOffset: `-${config.ringWidth}px`,
        outlineStyle: "solid" as const,
        outlineWidth: `${config.ringWidth}px`,
      }
    : undefined;

  return (
    <Avatar className={cn(config.className, className)} style={ringStyle}>
      {Boolean(avatar) && (
        <img
          alt={alt}
          className="aspect-square size-full object-cover"
          height={config.imgSize}
          src={avatar}
          width={config.imgSize}
        />
      )}
      <AvatarFallback
        className={cn(
          "font-semibold",
          statusColor ? statusColor.tailwind : "bg-primary/20 text-primary",
          config.textSize
        )}
      >
        {fallbackContent}
      </AvatarFallback>
    </Avatar>
  );
}
